import { useLendingMarkets } from '@aries/aptos-defi/lending/market';
import { isEmpty, last, reverse, sortBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { getProfileDetailKey, useProfileDetailHub } from './detail-hub';
import { useProfileList } from './list';

export const useProfileData = (walletAddress: string | undefined) => {
  const {
    profileList: metaList = [],
    addProfile,
    init: initMainProfile,
  } = useProfileList(walletAddress);

  const { activeMarketId } = useLendingMarkets();

  const { profileMap, triggerLoadProfileDetail: triggerLoadProfile } =
    useProfileDetailHub();

  // Subaccount address
  const [currentProfileKey, setCurrentProfileKey] = useState<string>();

  const profileList = useMemo(() => {
    if (!metaList || !walletAddress) return [];

    return metaList.map(
      ({
        profileName: name,
        address,
        profileDisplayName: displayName,
      }) => {
        const profileDetailKey = getProfileDetailKey(
          address,
          walletAddress,
          activeMarketId,
        );

        return {
          name,
          displayName,
          address,
          value: profileMap[profileDetailKey]?.value,
          loading: profileMap[profileDetailKey]?.loading,
        };
      },
    );
  }, [activeMarketId, metaList, profileMap, walletAddress]);

  const refreshCurrentProfile = async () => {
    const profile = metaList?.find(p => p.address === currentProfileKey);
    if (!profile || !walletAddress) return;

    await triggerLoadProfile(
      profile.address,
      profile.profileName,
      walletAddress,
      activeMarketId,
    );
  };

  const changeProfile = (address: string) => {
    const profile = profileList?.find(p => p.address === address);
    if (!profile || !walletAddress) return;

    setCurrentProfileKey(address);
    triggerLoadProfile(
      profile.address,
      profile.name,
      walletAddress,
      activeMarketId,
    );
  };

  useEffect(() => {
    if (
      metaList &&
      metaList.length > 0 &&
      !metaList.find(p => p.address === currentProfileKey)
    ) {
      setCurrentProfileKey(last(metaList)?.address);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaList, currentProfileKey]);

  useSWR(
    ['getProfilesDetails', metaList, walletAddress, activeMarketId],
    async () => {
      if (!metaList || isEmpty(metaList) || !walletAddress) {
        return true;
      }

      const metasByTime = sortBy(reverse([...metaList]), _meta =>
        profileMap[
          getProfileDetailKey(_meta.address, walletAddress, activeMarketId)
        ]?.value
          ? 1
          : -1,
      );
      for (const meta of metasByTime) {
        // eslint-disable-next-line no-await-in-loop
        await triggerLoadProfile(
          meta.address,
          meta.profileName,
          walletAddress,
          activeMarketId,
        );
      }

      return true;
    },
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
    },
  );

  return {
    hasInitialized: (profileList?.length ?? 0) > 0,
    profileList,
    currentProfileKey,
    initMainProfile,
    addProfile,
    changeProfile,
    refreshCurrentProfile,
  };
};
